/** Search, read and create (PR #220): every service that reads the evidence field takes these. */
export const LOCAL_FILE_TASK_TOOLS = ['search_files', 'read_file', 'create_folder_structure'];
/** The local actions on gyro-local-files (PR #367): only a service that reports localActionEvidence takes these. */
export const LOCAL_FILE_ACTION_TOOLS = ['list_folder', 'rename_items', 'move_items', 'copy_items', 'delete_items', 'write_file', 'open_item'];
/** gyro-local-commands' one tool (PR #367), also only for a service that reports localActionEvidence. */
export const LOCAL_COMMAND_TOOL = 'run_powershell';
/** What the widget may say about an MCP server or tool name: the service's own limits (localFileEvidence.ts). */
const MCP_NAME_MAX = { server: 100, tool: 200 };
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/u;
const printable = (value, max) => typeof value === 'string' && value.trim().length > 0 && value.length <= max && !CONTROL_CHARACTER.test(value);
/** The continuation tool a reviewed action ran, or null when its result cannot continue a task. */
export function localContinuationTool(action) {
    if (action.tool !== 'mcp_call')
        return null;
    const server = typeof action.input.server === 'string' ? action.input.server.trim().toLowerCase() : '';
    const tool = action.input.tool;
    if (typeof tool !== 'string')
        return null;
    if (server === 'gyro-local-files' && [...LOCAL_FILE_TASK_TOOLS, ...LOCAL_FILE_ACTION_TOOLS].includes(tool))
        return tool;
    if (server === 'gyro-local-commands' && tool === LOCAL_COMMAND_TOOL)
        return LOCAL_COMMAND_TOOL;
    return null;
}
/**
 * The call a reviewed action is, when its verified result can continue the chat: a local file task, or any other
 * mcp_call whose server and tool names are the catalog's own (printable, within the service's limits). Null for every
 * other action (its outcome is its "Done" line).
 */
export function continuationTarget(action) {
    if (action.tool !== 'mcp_call')
        return null;
    const local = localContinuationTool(action);
    const server = typeof action.input.server === 'string' ? action.input.server.trim() : '';
    if (local)
        return { kind: 'local-file', server, tool: local };
    if (server.toLowerCase() === 'gyro-local-files' || server.toLowerCase() === 'gyro-local-commands')
        return null;
    const tool = action.input.tool;
    return printable(server, MCP_NAME_MAX.server) && printable(tool, MCP_NAME_MAX.tool) ? { kind: 'mcp', server, tool } : null;
}
/**
 * How long a chat waits for a confirmed call on the PC to come back with its result before it stops waiting on its own
 * (owner, 2026-10-18: a confirmed revit_status whose result never came back left the chat at "Done - ..." with the composer
 * locked): WAIT_AFTER_SETTLE_MS after the service listed the action done or failed without a result having arrived, and
 * WAIT_OVERALL_MS after the confirm whatever the service says. The person can end the wait sooner ("Stop waiting").
 */
export const WAIT_AFTER_SETTLE_MS = 3 * 60_000;
export const WAIT_OVERALL_MS = 10 * 60_000;
/** Unicode bidirectional controls, as the service strips them (localFileEvidence.ts stripBidi). */
const BIDI_OR_CONTROL = /[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069\u0000-\u001f\u007f]/gu;
/** A model-chosen server or tool name as the visible line shows it: no bidi or control characters, at most 60 characters. */
export function visibleName(name) {
    const clean = name.replace(BIDI_OR_CONTROL, '');
    return clean.length > 60 ? `${wellFormedPrefix(clean, 59)}…` : clean;
}
/** A lone UTF-16 surrogate: with the `u` flag a complete pair reads as one code point and never matches. */
const LONE_SURROGATE = /[\ud800-\udfff]/gu;
/**
 * At most `max` UTF-16 units of `text`, never ending inside a surrogate pair, with any lone surrogate replaced by U+FFFD.
 * The desktop agent relays continuation evidence through System.Text.Json, which refuses to re-serialize a lone
 * surrogate (JSON.stringify sends one as a bare \ud83d escape), so a cut that split an emoji made every retry of
 * Continue file task fail in the native proxy with the generic ask error.
 */
export function wellFormedPrefix(text, max) {
    let end = Math.min(text.length, Math.max(0, Math.floor(max)));
    const last = end > 0 ? text.charCodeAt(end - 1) : 0;
    if (end < text.length && last >= 0xd800 && last <= 0xdbff)
        end--;
    return text.slice(0, end).replace(LONE_SURROGATE, '�');
}
/** Inbox-only cards have no originating turn; never guess their task from the current draft or chat. */
export function localFileOriginalRequest(messages, actionId) {
    const origin = messages.findIndex(message => message.role === 'assistant' && Boolean(message.question)
        && message.actions?.some(action => action.id === actionId));
    if (origin < 0)
        return null;
    for (let index = origin - 1; index >= 0; index--) {
        const message = messages[index];
        if (message.role !== 'user')
            continue;
        const request = message.localFileTask ?? message.content;
        return request.trim() ? wellFormedPrefix(request, 1600) : null;
    }
    return null;
}
export function parseDesktopActionResult(value) {
    if (!value || typeof value !== 'object')
        return null;
    const item = value;
    if (typeof item.actionId !== 'string' || !/^[a-f0-9-]{36}$/i.test(item.actionId)
        || typeof item.ok !== 'boolean' || typeof item.verified !== 'boolean'
        || typeof item.message !== 'string' || !item.message.trim() || item.message.length > 7000)
        return null;
    return { actionId: item.actionId, ok: item.ok, verified: item.verified, message: item.message };
}
/** Session-only association: the server owns action identity; native owns the execution result.
 * Neither a receipt-looking chat message nor a successful HTTP request establishes completion. */
export class LocalFileContinuations {
    entries = new Map();
    bind(action, owner, conversation, originalRequest, now = Date.now()) {
        const target = continuationTarget(action);
        if (!target || !owner || !conversation || !originalRequest.trim())
            return;
        if (this.entries.has(action.id))
            return;
        if (this.entries.size >= 32)
            this.entries.delete(this.entries.keys().next().value);
        this.entries.set(action.id, { actionId: action.id, owner, conversation, ...target, originalRequest: wellFormedPrefix(originalRequest, 1600), complete: false, boundAt: now });
    }
    receive(result, owner) {
        const entry = this.entries.get(result.actionId);
        if (!entry || entry.owner !== owner)
            return null;
        entry.native = result;
        const continuation = this.get(result.actionId, owner, entry.conversation);
        return { conversation: entry.conversation, continuation };
    }
    /** `completed`: the service settled the action done. `failed`: it settled it failed (not reconciliation): a verified
     * error continues an MCP call's chat (kind 'mcp'); a local file task continues only from a completed call. */
    settle(actionId, owner, conversation, completed, failed = false, now = Date.now()) {
        const entry = this.entries.get(actionId);
        if (!entry || entry.owner !== owner || entry.conversation !== conversation)
            return null;
        entry.complete = completed || (failed && entry.kind === 'mcp');
        // The first time the service lists it done or failed starts the shorter clock (the inbox re-reports it on every poll).
        if ((completed || failed) && entry.settledAt === undefined)
            entry.settledAt = now;
        return this.get(actionId, owner, conversation);
    }
    /** When a wait ends on its own: the shorter clock once the service settled the action, the overall one from the confirm. */
    static endsAt(entry) {
        const overall = entry.boundAt + WAIT_OVERALL_MS;
        return entry.settledAt === undefined ? overall : Math.min(overall, entry.settledAt + WAIT_AFTER_SETTLE_MS);
    }
    /** The MCP calls this chat is still waiting on: bound here, not yet continuable (no verified result with a settled action). */
    waiting(owner, conversation) {
        const out = [];
        for (const entry of this.entries.values()) {
            if (entry.kind !== 'mcp' || entry.owner !== owner || entry.conversation !== conversation)
                continue;
            if (this.get(entry.actionId, owner, conversation))
                continue;
            out.push({ actionId: entry.actionId, server: entry.server, tool: entry.tool, settled: entry.settledAt !== undefined, endsAt: LocalFileContinuations.endsAt(entry) });
        }
        return out;
    }
    /** The waits (any chat of this owner, or any owner when none is given) whose clock has run out at `now`. */
    expiredWaits(now = Date.now(), owner) {
        const out = [];
        for (const entry of this.entries.values()) {
            if (entry.kind !== 'mcp' || (owner !== undefined && entry.owner !== owner))
                continue;
            if (this.get(entry.actionId, entry.owner, entry.conversation))
                continue;
            const endsAt = LocalFileContinuations.endsAt(entry);
            if (now >= endsAt)
                out.push({ actionId: entry.actionId, server: entry.server, tool: entry.tool, settled: entry.settledAt !== undefined, endsAt });
        }
        return out;
    }
    /** End a wait: the binding is dropped (a late result continues nothing, nothing runs again). What it was, for the chat's line. */
    forget(actionId) {
        const entry = this.entries.get(actionId);
        if (!entry)
            return null;
        this.entries.delete(actionId);
        return { owner: entry.owner, conversation: entry.conversation, server: entry.server, tool: entry.tool };
    }
    get(actionId, owner, conversation) {
        const entry = this.entries.get(actionId);
        if (!entry || entry.owner !== owner || entry.conversation !== conversation || !entry.complete || !entry.native?.verified)
            return null;
        // A local file task's evidence is a success's only (its errors are completed outcomes with their own codes, or unverified).
        if (entry.kind === 'local-file' && !entry.native.ok)
            return null;
        return { actionId, kind: entry.kind, ok: entry.native.ok, server: entry.server, tool: entry.tool, originalRequest: entry.originalRequest, result: entry.native.message };
    }
    conversationFor(actionId, owner) {
        const entry = this.entries.get(actionId);
        return entry?.owner === owner ? entry.conversation : null;
    }
    clear() { this.entries.clear(); }
}
export const LOCAL_FILE_EVIDENCE_MAX_REQUEST = 600;
export const LOCAL_FILE_EVIDENCE_MAX_RESULT = 7000;
/** The visible user turn of a continuation; the evidence travels beside it, not inside it. */
export const LOCAL_FILE_CONTINUATION_MESSAGE = 'Continue the file task using this result.';
/** The visible user turn: the file task's fixed line, or the call the chat is continuing from. `serverLabel`: the server's
 * display name when the widget knows one (mcpDisplayNames.ts), so the line never shows a key like "revit-ai" for a server that has a name. */
export function continuationMessage(result, serverLabel) {
    if (result.kind !== 'mcp')
        return LOCAL_FILE_CONTINUATION_MESSAGE;
    return `Continue with the ${result.ok ? 'result of' : 'error from'} ${visibleName(result.tool)} on ${serverLabel ? visibleName(serverLabel) : visibleName(result.server)}.`;
}
export const LOCAL_FILE_EVIDENCE_UNSUPPORTED = 'This Gyro service cannot take a file result yet, so nothing was sent. The result stays here; use Continue file task again after the service is updated.';
export const LOCAL_FILE_EVIDENCE_UNREACHABLE = 'Gyro could not check that the service can take this file result, so nothing was sent. Try Continue file task again in a moment.';
export const MCP_EVIDENCE_UNSUPPORTED = 'This Gyro service cannot take a tool result yet, so nothing was sent. The result stays here; use Continue with this result again after the service is updated.';
export const MCP_EVIDENCE_UNREACHABLE = 'Gyro could not check that the service can take this tool result, so nothing was sent. Try Continue with this result again in a moment.';
/** Why a continuation was not sent, in the words of its kind. */
export function evidenceRefusalText(kind, service) {
    if (kind === 'mcp')
        return service === 'unsupported' ? MCP_EVIDENCE_UNSUPPORTED : MCP_EVIDENCE_UNREACHABLE;
    return service === 'unsupported' ? LOCAL_FILE_EVIDENCE_UNSUPPORTED : LOCAL_FILE_EVIDENCE_UNREACHABLE;
}
/**
 * Whether the service reads the continuation field, asked of the service itself (its /v1/status says
 * localFileEvidence) each time a continuation is about to be sent. A gyro-svc older than this widget ignores the field
 * and would answer without the file result, whether the widget reached it first (deploy order) or the service was
 * rolled back after the panel loaded, so a continuation is never sent to a service that has not just said it reads it.
 */
export async function localFileEvidenceService(status, tool = 'read_file', kind = 'local-file') {
    try {
        const reported = await status();
        if (reported.localFileEvidence !== true)
            return 'unsupported';
        // Any other MCP server's result names its server, which a service without mcpCallEvidence answers 400 for.
        if (kind === 'mcp')
            return reported.mcpCallEvidence === true ? 'supported' : 'unsupported';
        // A local action's result (PR #367) also needs localActionEvidence: a service that reads only search, read and create
        // answers 400 for any other tool.
        const task = LOCAL_FILE_TASK_TOOLS.includes(tool);
        return task || reported.localActionEvidence === true ? 'supported' : 'unsupported';
    }
    catch {
        return 'unreachable';
    }
}
/** Evidence travels with the new request, because a stored transcript can supersede client history. Both texts are cut
 * at whole characters and carry no lone surrogate, so the native proxy can always relay them. */
export function localFileEvidence(result) {
    return {
        actionId: result.actionId,
        tool: result.tool,
        ...(result.kind === 'mcp' ? { server: result.server } : {}),
        originalRequest: wellFormedPrefix(result.originalRequest, LOCAL_FILE_EVIDENCE_MAX_REQUEST),
        originalRequestTruncated: result.originalRequest.length > LOCAL_FILE_EVIDENCE_MAX_REQUEST,
        result: wellFormedPrefix(result.result, LOCAL_FILE_EVIDENCE_MAX_RESULT),
        resultTruncated: result.result.length > LOCAL_FILE_EVIDENCE_MAX_RESULT,
    };
}
//# sourceMappingURL=localFileContinuation.js.map